import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  FeedingTubePreferencesInfoRec,
  feedingTubePreferencesService,
} from '../../../../services/feedingTubePreferencesInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('FeedingTubePreferencesData');

type FeedingTubePreferencesDataScreenRouteProp = RouteProp<RootStackParamList, 'FeedingTubePreferencesData'>;

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
  feedingTubePreferencesId?: number;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const FeedingTubePreferencesDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<FeedingTubePreferencesDataScreenRouteProp>();
  const [feedingTubePreferencesInfo, setFeedingTubePreferencesInfo] = useState<FeedingTubePreferencesInfoRec | null>(null);
  const [loading, setLoading] = useState(true);

  const feedingTubePreferencesId = route.params?.feedingTubePreferencesId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadFeedingTubePreferencesInfo();
  }, []);

  const loadFeedingTubePreferencesInfo = async () => {
    try {
      if (feedingTubePreferencesId) {
        const info = await feedingTubePreferencesService.getFeedingTubePreferencesById(feedingTubePreferencesId, targetUserId);
        setFeedingTubePreferencesInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await feedingTubePreferencesService.getAllFeedingTubePreferences(targetUserId);
        if (allInfo.length > 0) {
          setFeedingTubePreferencesInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading feeding tube preferences info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (feedingTubePreferencesInfo) {
      // Map the data from feedingTubePreferencesInfo to the format expected by FeedingTubePreferencesInputScreen
      const mappedData = {
        id: feedingTubePreferencesInfo.id,
        feedingTubeType: feedingTubePreferencesInfo.feedingTubeType,
        locationOfDirective: feedingTubePreferencesInfo.locationOfDirective,
        specialInstructions: feedingTubePreferencesInfo.specialInstructions,
        feedingTubeDetails: feedingTubePreferencesInfo.feedingTubeDetails,
        notes: feedingTubePreferencesInfo.notes,
      };

      navigation.navigate('FeedingTubePreferencesInput', {
        feedingTubePreferencesData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('FeedingTubePreferencesInput');
    }
  };

  const handleDelete = () => {
    if (feedingTubePreferencesInfo) {
      Alert.alert(
        'Delete Feeding Tube Preferences',
        'Are you sure you want to delete these feeding tube preferences? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await feedingTubePreferencesService.deleteFeedingTubePreferences(feedingTubePreferencesInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await feedingTubePreferencesService.getAllFeedingTubePreferences();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('feeding_tube_preferences');
                }

                (navigation as any).navigate('FinalCare', { screen: 'FinalCareChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting feeding tube preferences info:', error);
                Alert.alert('Error', 'Failed to delete feeding tube preferences');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    (navigation as any).navigate('FinalCare', { screen: 'FinalCareChecklist', params: { refreshKey: Date.now() } });
  };

  const formatFeedingTubeType = (feedingTubeType?: string): string => {
    if (!feedingTubeType) return 'Not specified';

    const typeMapping: Record<string, string> = {
      'temporary': 'Temporary Feeding Tube',
      'long_term': 'Long Term Feeding Tube',
      'trial_period': 'Trial Period',
      'decline': 'Decline Feeding Tube',
      'family_decide': 'Let Family Decide'
    };

    return typeMapping[feedingTubeType] || feedingTubeType;
  };

  const formatFeedingTubeDetails = (feedingTubeDetails: any, feedingTubeType?: string): string => {
    if (!feedingTubeDetails || typeof feedingTubeDetails !== 'object') {
      return 'Not specified';
    }

    const details: string[] = [];

    if (feedingTubeType === 'temporary') {
      if (feedingTubeDetails.acceptableCircumstances) {
        details.push(`Acceptable Circumstances: ${feedingTubeDetails.acceptableCircumstances}`);
      }
      if (feedingTubeDetails.maxDuration) {
        details.push(`Maximum Duration: ${feedingTubeDetails.maxDuration}`);
      }
      if (feedingTubeDetails.specificConditions) {
        details.push(`Specific Conditions: ${feedingTubeDetails.specificConditions}`);
      }
      if (feedingTubeDetails.discussedWithHealthcareProvider !== undefined) {
        details.push(`Discussed with Healthcare Provider: ${feedingTubeDetails.discussedWithHealthcareProvider ? 'Yes' : 'No'}`);
      }
    } else if (feedingTubeType === 'long_term') {
      if (feedingTubeDetails.warrantsConditions) {
        details.push(`Warrants Conditions: ${feedingTubeDetails.warrantsConditions}`);
      }
      if (feedingTubeDetails.tubeTypePreferences) {
        details.push(`Tube Type Preferences: ${feedingTubeDetails.tubeTypePreferences}`);
      }
      if (feedingTubeDetails.qualityOfLifeFactors) {
        details.push(`Quality of Life Factors: ${feedingTubeDetails.qualityOfLifeFactors}`);
      }
      if (feedingTubeDetails.ongoingCarePreferences) {
        details.push(`Ongoing Care Preferences: ${feedingTubeDetails.ongoingCarePreferences}`);
      }
    } else if (feedingTubeType === 'trial_period') {
      if (feedingTubeDetails.trialDuration) {
        details.push(`Trial Duration: ${feedingTubeDetails.trialDuration}`);
      }
      if (feedingTubeDetails.continueCriteria) {
        details.push(`Continue Criteria: ${feedingTubeDetails.continueCriteria}`);
      }
      if (feedingTubeDetails.evaluationFactors) {
        details.push(`Evaluation Factors: ${feedingTubeDetails.evaluationFactors}`);
      }
      if (feedingTubeDetails.decisionMakingProcess) {
        details.push(`Decision Making Process: ${feedingTubeDetails.decisionMakingProcess}`);
      }
    } else if (feedingTubeType === 'decline') {
      if (feedingTubeDetails.reasonsForDeclining) {
        details.push(`Reasons for Declining: ${feedingTubeDetails.reasonsForDeclining}`);
      }
      if (feedingTubeDetails.exceptions) {
        details.push(`Exceptions: ${feedingTubeDetails.exceptions}`);
      }
      if (feedingTubeDetails.alternativeNutritionMethods) {
        details.push(`Alternative Nutrition Methods: ${feedingTubeDetails.alternativeNutritionMethods}`);
      }
      if (feedingTubeDetails.discussedAlternatives !== undefined) {
        details.push(`Discussed Alternatives: ${feedingTubeDetails.discussedAlternatives ? 'Yes' : 'No'}`);
      }
    } else if (feedingTubeType === 'family_decide') {
      if (feedingTubeDetails.specificDecisionMaker) {
        details.push(`Specific Decision Maker: ${feedingTubeDetails.specificDecisionMaker}`);
      }
      if (feedingTubeDetails.factorsToConsider) {
        details.push(`Factors to Consider: ${feedingTubeDetails.factorsToConsider}`);
      }
      if (feedingTubeDetails.circumstancesToKnow) {
        details.push(`Circumstances to Know: ${feedingTubeDetails.circumstancesToKnow}`);
      }
      if (feedingTubeDetails.valuesDiscussed !== undefined) {
        details.push(`Values Discussed: ${feedingTubeDetails.valuesDiscussed ? 'Yes' : 'No'}`);
      }
    }

    return details.length > 0 ? details.join('\n\n') : 'Not specified';
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Feeding Tube Preferences Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Feeding Tube Preferences Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {feedingTubePreferencesInfo ? (
            <>
              <InfoItem label="Feeding Tube Type" value={formatFeedingTubeType(feedingTubePreferencesInfo.feedingTubeType)} />

              {feedingTubePreferencesInfo.locationOfDirective && (
                <InfoItem label="Location of Directive" value={feedingTubePreferencesInfo.locationOfDirective} />
              )}

              {feedingTubePreferencesInfo.specialInstructions && (
                <InfoItem label="Special Instructions" value={feedingTubePreferencesInfo.specialInstructions} />
              )}

              {feedingTubePreferencesInfo.feedingTubeDetails && (
                <>
                  <Text style={styles.sectionTitle}>Feeding Tube Details</Text>
                  <InfoItem label="Details" value={formatFeedingTubeDetails(feedingTubePreferencesInfo.feedingTubeDetails, feedingTubePreferencesInfo.feedingTubeType)} />
                </>
              )}

              {feedingTubePreferencesInfo.notes && (
                <InfoItem label="Additional Notes" value={feedingTubePreferencesInfo.notes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No feeding tube preferences found.</Text>
              <TouchableOpacity
                style={[styles.button, styles.editButton, { marginTop: 20 }]}
                onPress={handleEdit}
              >
                <Text style={styles.buttonText}>Add Feeding Tube Preferences</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0', // Teal header background
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF', // White title on teal background
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF', // White content area
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280', // Gray label text
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F', // Dark blue value text
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0', // Teal separator line
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0', // Teal back button
  },
  deleteButton: {
    backgroundColor: '#FF4444', // Red delete button
  },
  editButton: {
    backgroundColor: '#FF9F5A', // Orange edit button
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(20),
    marginBottom: ms(15),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
